import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { fetchModelCards, type ModelCardFields } from '../../services/taModelCardsApi';
import type { BoardResponse, FeedbackItem } from '../../types/taActionBoard';
import type { ModalState } from './actionModalTypes';

/**
 * Pause before asking again for cards still `pending` / `unreadable`, or after a failed request.
 * Each ask already waits up to ~45 s server-side, so this only spaces out the failures; it grows so
 * a proxy that keeps failing is not hammered for as long as the board stays open.
 */
const RETRY_DELAYS_MS = [3_000, 10_000, 30_000, 60_000];
/** The most cards one `POST /model-cards` answers — the route keeps the first 60. */
const MODEL_CARDS_BATCH = 60;

type Patch = Partial<ModelCardFields> & { modelPending: false };

/** Feedback cards still waiting on the model, across every role of the board. */
export function pendingModelCards(board: BoardResponse | null): FeedbackItem[] {
  return (board?.roles || []).flatMap((r) => (r.feedback || []).filter((i) => i.modelPending));
}

/** The board with each patched card replaced; untouched roles and cards keep their identity. */
export function patchFeedbackCards(board: BoardResponse, patches: Record<string, Patch>): BoardResponse {
  return {
    ...board,
    roles: board.roles.map((role) =>
      (role.feedback || []).some((i) => patches[i.opportunityId])
        ? {
            ...role,
            feedback: role.feedback.map((i) => (patches[i.opportunityId] ? { ...i, ...patches[i.opportunityId] } : i)),
          }
        : role
    ),
  };
}

/**
 * Fill in the model half of Feedback cards the board shipped with `modelPending`.
 *
 * The board no longer waits for the summary and the rejection draft (proxy-server/lib/modelCards.js),
 * so this collects them afterwards and patches them into the board — and into an open Archive or
 * Move modal, which holds its own copy of the card. The board's localStorage cache then persists the
 * patched cards like any other change.
 *
 * A card that comes back `gone` is settled with its rating fallback rather than retried: nothing is
 * queued for it, and asking again would only repeat the answer. Every other card STAYS pending
 * until it resolves — it is asked for again while the board is open, and the board cache keeps the
 * flag, so a later load asks again too. Clearing it on a timeout would persist a card with no draft
 * that nothing ever fills in (CodeRabbit, PR #708).
 */
export function useModelCards(
  board: BoardResponse | null,
  setBoard: Dispatch<SetStateAction<BoardResponse | null>>,
  setModal: Dispatch<SetStateAction<ModalState>>
) {
  const pending = pendingModelCards(board);
  const recruiter = board?.recruiter.email || '';
  // A string key, so a re-render with the same pending set does not restart the request. The rating
  // is in it because the proxy derives the decision from the rating sent: a refresh that changes it
  // must not be overwritten by an answer computed from the old one (CodeRabbit, PR #708).
  const key = pending.map((i) => `${i.opportunityId}:${i.ratingLabel ?? ''}`).join(',');

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    const items = pendingModelCards(board);

    void (async () => {
      let remaining = items;
      for (let attempt = 0; remaining.length > 0 && !cancelled; attempt++) {
        if (attempt > 0) {
          const delay = RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)];
          await new Promise((r) => setTimeout(r, delay));
        }
        // EVERY card, in batches the route accepts, side by side — a first batch that stays pending
        // must not keep the rest from ever being asked for (CodeRabbit, PR #708). A failed batch
        // answers nothing, so its cards are simply asked for again on the next round.
        const batches = [];
        for (let i = 0; i < remaining.length; i += MODEL_CARDS_BATCH) batches.push(remaining.slice(i, i + MODEL_CARDS_BATCH));
        const answers = await Promise.all(batches.map((b) => fetchModelCards(b).catch(() => ({}))));
        if (cancelled) return;
        const cards = Object.assign({}, ...answers) as Awaited<ReturnType<typeof fetchModelCards>>;
        const patches: Record<string, Patch> = {};
        for (const item of remaining) {
          const card = cards[item.opportunityId];
          if (card?.state === 'ready') patches[item.opportunityId] = { ...card.fields, modelPending: false };
          // Only an explicit `gone` settles a card. An id the proxy did not answer stays pending.
          else if (card?.state === 'gone') patches[item.opportunityId] = { modelPending: false };
        }
        remaining = remaining.filter((i) => !patches[i.opportunityId]);
        if (Object.keys(patches).length === 0) continue;
        // Only onto the board these cards came from — the recruiter may have switched boards.
        setBoard((b) => (b && b.recruiter.email === recruiter ? patchFeedbackCards(b, patches) : b));
        setModal((m) =>
          m && (m.kind === 'archive' || m.kind === 'move') && patches[m.item.opportunityId]
            ? { ...m, item: { ...m.item, ...patches[m.item.opportunityId] } }
            : m
        );
        // Patching changes `key`, which re-runs this effect for whatever is left.
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
    // `board` is read once per pending set; `key` and `recruiter` are what changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, recruiter, setBoard, setModal]);
}
